/**
 * B930 — the walk's view, named twice: once as a KEY (so the page's focus
 * can follow a swap, `useViewFocus`) and once as a TITLE (so the tab says
 * where the buyer is — WCAG 2.4.2; it read "Checkout" on every view).
 */
import type { ScreenKind } from "../ui/screens.js";

export interface WalkView {
  readonly screen: ScreenKind;
  /** The product whose tickets step is open (browse only). */
  readonly productId?: string | undefined;
  /** A busy face standing in for the view ("Adding to your cart…"). */
  readonly busy?: boolean | undefined;
  /** The booking sheet's step (embed dialog). */
  readonly dialogStep?: string | undefined;
  /** The sheet's group walk. */
  readonly groupStage?: string | undefined;
  /** A journey's stage (outbound / return). */
  readonly stage?: string | undefined;
}

/** One string per view the buyer can tell apart; equal views, equal keys. */
export function walkViewKey(v: WalkView): string {
  return [v.screen, v.productId ?? "", v.busy === true ? "busy" : "", v.dialogStep ?? "", v.groupStage ?? "", v.stage ?? ""].join("|");
}

const SCREEN_TITLE: Record<ScreenKind, string> = {
  browse: "Tickets",
  cart: "Your cart",
  checkout_in_flight: "Pay",
  payment: "Pay",
  confirmed: "You’re booked",
  expired: "Your hold expired",
};

/** The tab's title: the view first, then the business's name when one is known. */
export function storefrontTitle(v: {
  readonly screen: ScreenKind;
  readonly productName?: string | undefined;
  readonly show?: boolean | undefined;
  readonly businessName?: string | undefined;
}): string {
  const list = v.show === true ? "Shows" : "Tickets";
  const view =
    v.screen === "browse"
      ? v.productName !== undefined && v.productName.trim() !== ""
        ? `${v.productName.trim()} · ${list}`
        : list
      : SCREEN_TITLE[v.screen];
  const seller = v.businessName?.trim() ?? "";
  return seller === "" ? view : `${view} · ${seller}`;
}

/** The view, then the venue's name when one is known. */
const withSeller = (view: string, businessName: string | undefined): string => {
  const seller = businessName?.trim() ?? "";
  return seller === "" ? view : `${view} · ${seller}`;
};

/** B932 — /memberships' views (the app's `View` kinds). */
export type MembershipsViewKind = "offer" | "checkout" | "paying" | "card" | "finishing" | "done";

const MEMBERSHIPS_TITLE: Record<MembershipsViewKind, string> = {
  offer: "Memberships",
  checkout: "Who’s covered · Memberships",
  paying: "Pay · Memberships",
  card: "Pay · Memberships",
  finishing: "Finishing your membership",
  done: "You’re a member",
};

/** B932 — the memberships page's tab title; it read "Checkout" on every step. */
export function membershipsTitle(kind: MembershipsViewKind, businessName?: string): string {
  return withSeller(MEMBERSHIPS_TITLE[kind], businessName);
}

/** B932 — the buyer portal's pages: sign-in (ask / sent), My orders, an order, the membership card. */
export type MeViewKind = "unknown" | "sign-in" | "sent" | "orders" | "order" | "membership";

const ME_TITLE: Record<MeViewKind, string> = {
  unknown: "Your tickets",
  "sign-in": "Sign in · Your tickets",
  sent: "Check your email · Your tickets",
  orders: "Your orders",
  order: "Your order",
  membership: "Your membership",
};

/** B932 — the portal's tab title; it read "Checkout" on /me and /me/membership. */
export function meTitle(kind: MeViewKind, businessName?: string): string {
  return withSeller(ME_TITLE[kind], businessName);
}
